import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "../firebase/config";
import "../styles/auth.css";

const AuthHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [validCode, setValidCode] = useState(false);

  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    const verifyCode = async () => {
      if (mode !== "resetPassword" || !oobCode) {
        setError("Invalid password reset link.");
        setVerifying(false);
        return;
      }

      try {
        // Verify the password reset code and get the email
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setValidCode(true);
      } catch (error: any) {
        console.error("Code verification error:", error);

        switch (error.code) {
          case "auth/expired-action-code":
            setError(
              "This password reset link has expired. Please request a new one.",
            );
            break;
          case "auth/invalid-action-code":
            setError(
              "This password reset link is invalid. Please request a new one.",
            );
            break;
          case "auth/user-disabled":
            setError("This account has been disabled.");
            break;
          case "auth/user-not-found":
            setError("No account found for this reset link.");
            break;
          default:
            setError("Invalid password reset link. Please request a new one.");
        }
      } finally {
        setVerifying(false);
      }
    };

    verifyCode();
  }, [mode, oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!newPassword) {
      setError("Please enter a new password");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!oobCode) {
      setError("Invalid reset code");
      return;
    }

    setLoading(true);

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccess(true);
    } catch (error: any) {
      console.error("Password reset error:", error);

      switch (error.code) {
        case "auth/expired-action-code":
          setError(
            "This password reset link has expired. Please request a new one.",
          );
          break;
        case "auth/invalid-action-code":
          setError(
            "This password reset link is invalid. Please request a new one.",
          );
          break;
        case "auth/weak-password":
          setError("Password is too weak. Please choose a stronger password.");
          break;
        default:
          setError(
            "An error occurred while resetting your password. Please try again.",
          );
      }
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="w-full bg-base-100">
        <div className="flex items-center justify-center max-w-xl mx-auto px-4 min-h-screen">
          <div className="p-8 w-full mx-auto text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
            <h1 className="text-2xl font-semibold text-base-content mb-2">
              Verifying Reset Link
            </h1>
            <p className="text-base-content/70">
              Please wait while we verify your password reset link...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="w-full bg-base-100">
        <div className="flex items-center justify-center max-w-xl mx-auto px-4 min-h-screen">
          <div className="p-8 w-full mx-auto text-center">
            <div className="text-green-500 text-6xl mb-4"></div>
            <h1 className="text-2xl font-semibold text-base-content mb-4">
              Password Reset Successful
            </h1>
            <p className="text-base-content/70 mb-6">
              Your password has been successfully reset. You can now sign in
              with your new password.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="btn btn-primary"
            >
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!validCode) {
    return (
      <div className="w-full bg-base-100">
        <div className="flex items-center justify-center max-w-xl mx-auto px-4 min-h-screen">
          <div className="p-8 w-full mx-auto text-center">
            <div className="text-red-500 text-6xl mb-4"></div>
            <h1 className="text-2xl font-semibold text-base-content mb-4">
              Invalid Reset Link
            </h1>
            <p className="text-base-content/70 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/forgot-password")}
                className="btn btn-primary w-full"
              >
                Request New Reset Link
              </button>
              <button
                onClick={() => navigate("/login")}
                className="btn btn-ghost w-full"
              >
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-base-100">
      <div className="flex items-center justify-center max-w-xl mx-auto px-4 min-h-screen">
        <div className="p-8 w-full mx-auto">
          <h1 className="text-2xl font-semibold text-center text-base-content mb-2">
            Reset Your Password
          </h1>
          <p className="text-center mb-6 text-base-content/70">
            Enter your new password for {email}
          </p>

          {error && (
            <div className="border border-red-200 bg-red-500/10 rounded-xl p-4 mb-4 text-red-500 text-sm">
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 w-full">
            <div className="form-group w-full">
              <div className="new-form-group w-full">
                <input
                  className="form-element"
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  placeholder="Enter new password"
                />
                <label htmlFor="newPassword">New Password</label>
              </div>
            </div>

            <div className="form-group w-full">
              <div className="new-form-group w-full">
                <input
                  className="form-element"
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  placeholder="Confirm new password"
                />
                <label htmlFor="confirmPassword">Confirm Password</label>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-base-100 mr-2"></div>
                  Resetting password...
                </div>
              ) : (
                "Reset password"
              )}
            </button>

            <div className="text-center pt-4">
              <p className="text-sm text-base-content/70">
                Remember your password?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-base-content hover:text-base-content/80 font-medium"
                >
                  Back to Sign In
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuthHandler;
